import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useAnalyticsPageView } from '@chilecompra/react-kit/analytics'
import { Button, Input, useInput, useSelect, useViewport, Select } from '@chilecompra/react-kit/components'
import {
  checkInputErrorMaxLength,
  checkInputErrorMinLength,
  checkInputErrorOnlyNumbers,
  checkInputErrorRequired,
  formatOnlyNumbers,
  checkInputErrorRegEx
} from '@chilecompra/react-kit/utils'
import { NATIONAL_GENDER_OPTIONS } from '../../config/settings/constants'
import { isLocalEnv, REACT_APP_FEATURE_FLAG_ENABLE_CIVIL_REGISTRY } from '../../config/settings/environment'
import { ONLY_NAMES_PEOPLE_LETTERS } from '../../modules/utils/regexes'
import { onResetRegisterWizardState, onSaveTemporaryPersonalInformation } from './RegisterWizard.actions'
import {
  DesktopNavigation,
  Hidden,
  Image,
  Instructions,
  MobileNavigation,
  PhoneNumberPrefix,
  Root,
  Section,
  Title
} from './RegisterWizardPersonalInformation.styles'

/**
 * The RegisterWizardPersonalInformation's container.
 */
const RegisterWizardPersonalInformation = props => {
  const { onChangeStep } = props
  const { error, guest, loading, registerType } = useSelector(state => state.registerWizard)
  const { name, firstLastName, secondLastName } = useSelector(state => state.registerWizard.guest)
  const { size } = useViewport()
  const dispatch = useDispatch()

  const isEnableCivilRegistry =
    REACT_APP_FEATURE_FLAG_ENABLE_CIVIL_REGISTRY === true || REACT_APP_FEATURE_FLAG_ENABLE_CIVIL_REGISTRY === 'true'

  const {
    error: nameError,
    value: nameValue,
    onChange: handleNameChange
  } = useInput({
    errorCallbacks: [
      checkInputErrorRequired(),
      checkInputErrorRegEx(ONLY_NAMES_PEOPLE_LETTERS, 'Solo se permiten letras, espacios y guiones (-).')
    ],
    formatCallbacks: [value => value.toUpperCase()],
    initialValue: name,
    overwriteCallback: () => name
  })
  const {
    error: firstLastNameError,
    value: firstLastNameValue,
    onChange: handleFirstLastNameChange
  } = useInput({
    errorCallbacks: [
      checkInputErrorRequired(),
      checkInputErrorRegEx(ONLY_NAMES_PEOPLE_LETTERS, 'Solo se permiten letras, espacios y guiones (-).')
    ],
    formatCallbacks: [value => value.toUpperCase()],
    initialValue: firstLastName,
    overwriteCallback: () => firstLastName
  })
  const { value: secondLastNameValue, onChange: handleSecondLastNameChange } = useInput({
    errorCallbacks: [
      checkInputErrorRegEx(ONLY_NAMES_PEOPLE_LETTERS, 'Solo se permiten letras, espacios y guiones (-).')
    ],
    formatCallbacks: [value => value.toUpperCase()],
    initialValue: secondLastName,
    overwriteCallback: () => secondLastName
  })
  const {
    error: phoneError,
    value: phoneValue,
    onChange: handlePhoneChange
  } = useInput({
    errorCallbacks: [
      checkInputErrorRequired(),
      checkInputErrorOnlyNumbers(),
      checkInputErrorMinLength(8, 'Este campo permite 8 dígitos.'),
      checkInputErrorMaxLength(8, 'Este campo permite 8 dígitos.')
    ],
    formatCallbacks: [formatOnlyNumbers]
  })

  const {
    error: genderError,
    value: genderValue,
    onChange: onGenderChange
  } = useSelect({
    initialValue: 1,
    errorCallbacks: [checkInputErrorRequired()]
  })

  const genderOptions = useMemo(() => NATIONAL_GENDER_OPTIONS.map(option => ({ ...option, name: option.name })), [])

  const handleBack = () => {
    dispatch(onResetRegisterWizardState())
    onChangeStep('welcome')
  }

  const handleNext = useCallback(
    event => {
      event.preventDefault()

      dispatch(
        onSaveTemporaryPersonalInformation({
          firstLastName: firstLastNameValue,
          gender: guest.gender || genderValue,
          name: nameValue,
          phone: phoneValue,
          phoneCode: '56-9',
          secondLastName: secondLastNameValue
        })
      )
      onChangeStep('email-verify')
    },
    [
      onSaveTemporaryPersonalInformation,
      firstLastNameValue,
      guest,
      nameValue,
      secondLastNameValue,
      phoneValue,
      genderValue
    ]
  )

  const canNotSubmit = useCallback(() => {
    const hadErrors = nameError !== '' || firstLastNameError !== '' || phoneError !== '' || genderError !== ''

    const hadInvalidValues = nameValue === '' || firstLastNameValue === '' || phoneValue === ''

    return hadErrors || hadInvalidValues || error
  }, [firstLastNameError, nameError, phoneError, nameValue, firstLastNameValue, phoneValue, error])

  const disabledSubmit = !isLocalEnv && canNotSubmit()

  useAnalyticsPageView('/paso/1/informacion_personal')

  return (
    <Root>
      {!loading && registerType === 'by-unique-pass'}
      <Section container>
        <Section item xs={12} md={6}>
          <Title>Sigamos</Title>
          <Instructions>Agrega tu número móvil a tu información personal.</Instructions>
          <Section
            container
            direction="column"
            margin="15px 0"
            maxWidth={size.isDownToMedium || size.isUpToSmall ? '384px' : '100%'}
          >
            <Section item>
              <Input
                autoFocus
                disabled={name !== ''}
                error={nameError}
                label="Nombre"
                loading={loading}
                onChange={handleNameChange}
                required
                value={nameValue}
              />
            </Section>
            <Section item>
              <Input
                disabled={firstLastName !== ''}
                error={firstLastNameError}
                label="Primer apellido"
                loading={loading}
                onChange={handleFirstLastNameChange}
                required
                value={firstLastNameValue}
              />
            </Section>
            <Section item>
              <Input
                disabled={secondLastName !== ''}
                label="Segundo apellido"
                loading={loading}
                onChange={handleSecondLastNameChange}
                value={secondLastNameValue}
              />
            </Section>
            <Section item>
              <Input
                prefix={<PhoneNumberPrefix>+56 9</PhoneNumberPrefix>}
                error={phoneError}
                label="Número de teléfono móvil "
                onChange={handlePhoneChange}
                placeholder="81234567"
                required
                value={phoneValue}
              />
            </Section>
            {(!isEnableCivilRegistry || !guest.gender) && (
              <Section item>
                <Select
                  required
                  error={genderError}
                  label="Sexo"
                  loading={loading}
                  onChange={onGenderChange}
                  options={genderOptions}
                  value={genderValue}
                />
              </Section>
            )}
          </Section>
        </Section>
        <Hidden smDown>
          <Section item xs={12} md={6} margin="-35px 0 0" padding="0 0 0 51px">
            <Image src="/assets/img/steps/paso2.svg?h=e57104cc1427561c7d89c6004abefe05" alt="Registro vía ClaveÚnica" />
          </Section>
        </Hidden>
      </Section>
      {!size.isTiny && (
        <Section container margin="48px 0 0" maxWidth="568px">
          <DesktopNavigation>
            <Button onClick={handleBack} type="button" variant="text" width="265px">
              Atrás
            </Button>
            <Button
              color="primary"
              disabled={disabledSubmit}
              margin="0 0 0 30px"
              type="button"
              width="265px"
              onClick={handleNext}
            >
              Siguiente
            </Button>
          </DesktopNavigation>
        </Section>
      )}
      {size.isTiny && (
        <MobileNavigation>
          <Button onClick={handleBack} type="button" variant="text" width="168px">
            Atrás
          </Button>
          <Button color="primary" disabled={disabledSubmit} type="button" width="168px" onClick={handleNext}>
            Siguiente
          </Button>
        </MobileNavigation>
      )}
    </Root>
  )
}

RegisterWizardPersonalInformation.defaultProps = {
  onChangeStep: () => undefined
}

RegisterWizardPersonalInformation.propTypes = {
  onChangeStep: PropTypes.func
}

export default RegisterWizardPersonalInformation
